import React, { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';

import { UserOutlined } from '@ant-design/icons';
import { Avatar, Skeleton, Tooltip } from 'antd';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { formatMessage } from 'umi';

import { useIntersectionObserver } from '@/hooks';
import UserPhotoService from '@/services/userPhoto';
import { ToColorFromTitleCaseLetters, ToNLetters } from '@/utils/infrastructure';
import { getColorPalette } from '@/utils/infrastructure/utilities/colors';
import photoReducer, { initialState } from './reducer';
import styles from './style.less';
import { Regexp } from '@/utils/infrastructure/utilities';

const UserPicture = (props) => {
  const containerRef = useRef(null);
  const { 'aria-label': ariaLabel, 'aria-describedby': ariaDescribedBy, onClick } = props;
  const [state, dispatch] = useReducer(photoReducer, initialState);
  const { loading, imageUrl, imageLoaded, hasError } = state;

  const photoService = UserPhotoService.getInstance();
  const { ref: intersectionRef, isVisible } = useIntersectionObserver({
    rootMargin: '100px',
    threshold: 0.1,
    enabled: !!props.id,
    once: true,
  });

  const showFallbackIcon = !loading && (!imageUrl || hasError) && !props.name && !props.fullName;
  const showInitials = !loading && (!imageUrl || hasError) && (props.name || props.fullName);
  const fallbackIcon = showFallbackIcon ? (
    <UserOutlined style={{ fontSize: props.size ? props.size / 2 : 18, margin: 'auto' }} />
  ) : null;

  const sizeStyle = useMemo(() => {
    if (!props.size) return {};
    if (props.styles) return props.styles;
    return { width: props.size };
  }, [props.size, props.styles]);

  const fallbackStyle = useMemo(() => {
    if (!showInitials) return {};

    const hex = ToColorFromTitleCaseLetters(props.name ?? props.fullName);
    const { textColor, backColor } = getColorPalette(hex);

    return {
      color: textColor,
      background: backColor,
      ...(props.size && { fontSize: props.size / 3 }),
    };
  }, [showInitials, props.name, props.fullName, props.size]);

  const accessibleAlt = useMemo(() => {
    // Describe current visual state for better accessibility
    const baseContext =
      props.name || props.fullName
        ? { id: 'component.user.nameProfilePhoto', values: { name: props.name ?? props.fullName } }
        : props.id
        ? { id: 'component.user.userIdProfilePhoto', values: { id: props.id } }
        : { id: 'component.user.userProfilePhoto', values: {} };

    // Add state context for screen readers
    if (showSkeleton) return formatMessage({ id: 'component.user.loadingProfilePhoto' });
    if (imageUrl && imageLoaded) return formatMessage({ id: baseContext.id }, baseContext.values);

    // Fallback state (initials or default icon)
    return formatMessage(
      { id: showInitials ? 'component.user.nameInitials' : 'component.user.defaultAvatar' },
      baseContext.values,
    );
  }, [props.name, props.fullName, props.id, showSkeleton, imageUrl, imageLoaded, showInitials]);

  const accessibleAriaLabel = useMemo(() => {
    if (ariaLabel) return ariaLabel;
    if (onClick) {
      return props.name || props.fullName
        ? formatMessage(
            { id: 'component.user.nameProfile' },
            { name: props.name ?? props.fullName },
          )
        : formatMessage({ id: 'component.user.userProfile' });
    }
    return accessibleAlt;
  }, [ariaLabel, onClick, props.name, props.fullName, accessibleAlt]);

  const loadPhoto = useCallback(async () => {
    try {
      if (!Regexp.isUUID(props.id)) {
        dispatch({ type: 'LOADING_COMPLETE' });
        return;
      }

      const result = await photoService.getUserPhoto(props.id, props.isAgent);
      if (result?.photoPublicUrl) {
        dispatch({ type: 'SET_PHOTO_URL', url: result.photoPublicUrl });
        // Preload the image to ensure it displays correctly
        const img = new Image();
        img.onload = () => dispatch({ type: 'PHOTO_LOADED' });
        img.onerror = () => dispatch({ type: 'PHOTO_ERROR' });
        img.src = result.photoPublicUrl;
      } else dispatch({ type: 'LOADING_COMPLETE' });
    } catch (e) {
      dispatch({ type: 'PHOTO_ERROR' });
    } finally {
      dispatch({ type: 'LOADING_COMPLETE' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.id, props.isAgent]);

  useEffect(() => {
    if (!props.id) {
      dispatch({ type: 'RESET_NO_ID' });
      return;
    }

    // Check cache first to avoid clearing cached data
    const cacheKey = `${props.isAgent ? 'agent' : 'user'}:${props.id}`;
    const cachedResult = photoService.cacheManager.get(cacheKey);
    if (cachedResult?.photoPublicUrl) {
      const isExpired = cachedResult.expiration
        ? Date.now() > new Date(cachedResult.expiration).getTime()
        : false;

      if (isExpired) {
        photoService.invalidateUserPhoto(String(props.id), props.isAgent);
        dispatch({ type: 'START_LOADING' });
        return;
      }

      const img = new Image();
      img.onload = () => dispatch({ type: 'SET_CACHED_PHOTO', url: cachedResult.photoPublicUrl });
      img.onerror = () => {
        photoService.invalidateUserPhoto(String(props.id), props.isAgent);
        dispatch({ type: 'START_LOADING' });
      };
      img.src = cachedResult.photoPublicUrl;
      return;
    }

    dispatch({ type: 'START_LOADING' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.id, props.isAgent]);

  useEffect(() => {
    if (!props.id || !isVisible || hasError || imageUrl || !loading) {
      return;
    }

    loadPhoto();
  }, [props.id, isVisible, hasError, imageUrl, loading, loadPhoto]);

  const showSkeleton = loading || (imageUrl && !imageLoaded && !hasError);
  const avatarChildren = showInitials ? ToNLetters(props.name ?? props.fullName) : null;

  const avatarComponent = (
    <div
      ref={(el) => {
        intersectionRef.current = el;
        containerRef.current = el;
      }}
      style={sizeStyle}
      className={classNames(styles.userPhoto, {
        [styles.clickable]: !!onClick || props.clickable,
        [styles.isAgent]: props.isAgent,
      })}
      role={props.role || (onClick ? 'button' : 'img')}
      tabIndex={onClick ? props.tabIndex ?? 0 : -1}
      aria-label={accessibleAriaLabel}
      aria-describedby={ariaDescribedBy}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {showSkeleton ? (
        <Skeleton.Avatar
          active
          size={props.size}
          shape={props.shape}
          className={styles.skeleton}
          aria-label={formatMessage({ id: 'component.user.loadingProfilePhoto' })}
        />
      ) : (
        <Avatar
          alt={accessibleAlt}
          size={props.size}
          shape={props.shape}
          src={imageUrl ?? props.iconUrl}
          icon={props.isAgent && !props.iconUrl ? '✨' : fallbackIcon}
          style={{ ...fallbackStyle, width: props.size, height: props.size }}
          className={classNames(styles.avatar, props.className, {
            [styles.hasPhoto]: imageUrl && imageLoaded && !hasError,
            [styles.fallbackState]: showFallbackIcon || showInitials,
            [styles.hidden]: props.hidden,
          })}
          onClick={onClick}
        >
          {avatarChildren}
        </Avatar>
      )}
    </div>
  );

  if (props.showTooltip) {
    return <Tooltip title={props.name ?? props.fullName}>{avatarComponent}</Tooltip>;
  }

  return avatarComponent;
};

UserPicture.defaultProps = {
  size: 36,
  showTooltip: true,
  shape: 'circle',
};

UserPicture.propTypes = {
  id: PropTypes.string,
  size: PropTypes.number,
  shape: PropTypes.oneOf(['circle', 'square']),
  className: PropTypes.string,
  onClick: PropTypes.func,
  name: PropTypes.string,
  fullName: PropTypes.string,
  'aria-label': PropTypes.string,
  'aria-describedby': PropTypes.string,
  role: PropTypes.string,
  showTooltip: PropTypes.bool,
  tabIndex: PropTypes.number,
};

export default React.memo(UserPicture);
